import { memo, useState } from 'react';
import { Form, Input, Row, Col, message } from 'antd';

import { Shipping, ShippingWay } from 'models/auction/Shipping';

import styles from './ShippingField.module.css';

interface ShippingFieldProps {
  shippingMethod: Shipping;
  createShippingMethod: (shippingMethod: Shipping) => void;
  updateShippingMethod: (keyDescription: string, newShippingMethod: Shipping) => void;
  removeShippingMethod: (type: ShippingWay, keyDescription: string) => void;
  checkDuplicateShippingMethod: (type: ShippingWay, description: string) => boolean;
  clearError?: () => void;
  closeInsertField?: () => void;
}

function ShippingField({
  shippingMethod,
  createShippingMethod,
  updateShippingMethod,
  removeShippingMethod,
  checkDuplicateShippingMethod,
  clearError,
  closeInsertField,
}: ShippingFieldProps) {
  /**
   * if keyDescription is empty string, it means this component used as insert field
   * else, it means this components used as update field
   */
  const keyDescription = shippingMethod.description;
  const isInsertField = keyDescription === '';

  const type = shippingMethod.type;
  const [description, _setDescription] = useState(keyDescription);
  const [cost, _setCost] = useState<number | null>(shippingMethod.cost);

  if (type === 'other') return <p>지원되지 않는 형식입니다</p>;

  const setDescription = (description: string) => {
    clearError?.();
    _setDescription(description);
  };

  const setCost = (cost: number) => {
    clearError?.();
    _setCost(cost);
  };

  const syncOnStore = () => {
    // no changed
    if (shippingMethod.description === description && shippingMethod.cost === cost) return;

    // wait until cost has value if type is parcel
    if (type === 'parcel' && cost === null) return;

    closeInsertField?.(); // always closeInsertField, if it exists

    if (description === '') {
      removeShippingMethod(type, keyDescription);
      message.warning('입력한 장소가 없어 자동 삭제됩니다.');
      return;
    }

    if (checkDuplicateShippingMethod(type, description)) {
      removeShippingMethod(type, keyDescription);
      message.warning('동일한 장소가 있어 자동 삭제됩니다.');
      return;
    }

    const newShippingMethod: Shipping = { type, description, cost };
    if (isInsertField) createShippingMethod(newShippingMethod);
    else updateShippingMethod(keyDescription, newShippingMethod);
  };

  return (
    <Row gutter={6}>
      <Col span={type === 'parcel' ? 16 : 24}>
        <Form.Item className={styles.field}>
          <Input
            placeholder={type === 'in_person' ? '직거래 가능한 장소를 입력해주세요' : '전 지역, 도서산간, 제주도 등'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={syncOnStore}
          />
        </Form.Item>
      </Col>
      {type === 'parcel' && (
        <Col span={8}>
          <Form.Item className={styles.field}>
            <Input
              type="number"
              placeholder="배송료"
              prefix="₩"
              value={cost ?? ''}
              onChange={(e) => setCost(parseInt(e.target.value) ?? 0)}
              onBlur={syncOnStore}
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  );
}

export default memo(ShippingField);
